import React, { useMemo } from "react";
import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";
import { Button } from "reactstrap";
import { useMeCore } from "../../../cores/me";
import { useSocialChatCore } from "../../../cores/socialChat";
import {
  WHATSAPP_COLOR,
  FACEBOOK_COLOR,
  INSTAGRAM_COLOR,
  LINKED_IN_COLOR,
  LINKED_IN_BUSINESS_COLOR,
} from "../../../constants/socialColors.constant";
import { Conversations } from "../../../cores/socialChat/interfaces/conversations-group.interface";
import { InputWembii } from "../InputWembii";
import { FilterOption } from "./filterOption/FilterOption";
import { useFilterDate } from "../../../hooks/useFilterDate/useFilterDate.hook";
import { Conversation } from "../../../cores/socialChat/interfaces/conversations.interface";

export type WebChatListFilter = "all" | "unread" | "unresolved";

type Props = {
  conversations: Conversations;
  searchText: string;
  onSearchChange: (value: string) => void;
  listFilter: WebChatListFilter;
  onListFilterChange: (f: WebChatListFilter) => void;
  onOpenAdvancedFilters: () => void;
};

export default function WebChatInboxToolbar({
  conversations,
  searchText,
  onSearchChange,
  listFilter,
  onListFilterChange,
  onOpenAdvancedFilters,
}: Props) {
  const intl = useIntl();
  const { socialSettings } = useMeCore();
  const {
    conversationStatus,
    selectedSocial,
    setSelectedSocial,
    dateFilter,
  } = useSocialChatCore();
  const { filterDateConversation } = useFilterDate(dateFilter);

  const configuredSocials = {
    whatsapp: !!socialSettings?.whatsapp,
    instagram: !!socialSettings?.instagramMensseger,
    facebook: !!socialSettings?.facebook,
    linkedin: !!socialSettings?.linkedIn,
    linkedinBusiness: !!socialSettings?.linkedInBusiness,
  };

  /** Icono a color solo si el canal está seleccionado; si está integrado pero no activo, apagado (gris). */
  const channelIconColor = (
    configured: boolean,
    isSelected: boolean,
    brandColor: string
  ) => {
    if (!configured) return "#dee2e6";
    return isSelected ? brandColor : "#adb5bd";
  };

  const bucketConversations = useMemo((): Conversation[] => {
    const bucket = conversations[selectedSocial] ?? {
      active: [],
      archived: [],
      inTrash: [],
    };
    return bucket[conversationStatus].filter(filterDateConversation);
  }, [
    conversations,
    selectedSocial,
    conversationStatus,
    filterDateConversation,
  ]);

  const hasUnread = useMemo(
    () => bucketConversations.some((c) => c.unreadCount > 0),
    [bucketConversations]
  );

  const tabs: { id: WebChatListFilter; labelId: string }[] = [
    { id: "unresolved", labelId: "app.chat.inboxTab.unresolved" },
    { id: "unread", labelId: "app.chat.inboxTab.unread" },
    { id: "all", labelId: "app.chat.inboxTab.all" },
  ];

  return (
    <div className="webchat-inbox-toolbar">
      <div className="webchat-inbox-toolbar__channels">
        <FilterOption
          label=""
          onClick={() => setSelectedSocial("all")}
          selected={selectedSocial === "all"}
          className="webchat-inbox-toolbar__channel"
          icon={
            <FontAwesomeIcon
              className="mx-1"
              icon={["fas", "comments"]}
              size="lg"
              color={selectedSocial !== "all" ? "#adb5bd" : "#0d6efd"}
            />
          }
        />
        <FilterOption
          label=""
          onClick={() => setSelectedSocial("web")}
          selected={selectedSocial === "web"}
          className="webchat-inbox-toolbar__channel"
          icon={
            <FontAwesomeIcon
              className="mx-1"
              icon={["fas", "globe"]}
              size="lg"
              color={selectedSocial !== "web" ? "#adb5bd" : "#0d4b7e"}
            />
          }
        />
        <FilterOption
          enabled={configuredSocials.whatsapp}
          label=""
          onClick={() => setSelectedSocial("whatsapp")}
          selected={selectedSocial === "whatsapp"}
          className="webchat-inbox-toolbar__channel"
          icon={
            <FontAwesomeIcon
              className="mx-1"
              icon={["fab", "whatsapp"]}
              size="lg"
              color={channelIconColor(
                configuredSocials.whatsapp,
                selectedSocial === "whatsapp",
                WHATSAPP_COLOR
              )}
            />
          }
        />
        <FilterOption
          enabled={configuredSocials.facebook}
          label=""
          onClick={() => setSelectedSocial("facebook")}
          selected={selectedSocial === "facebook"}
          className="webchat-inbox-toolbar__channel"
          icon={
            <FontAwesomeIcon
              className="mx-1"
              icon={["fab", "facebook"]}
              size="lg"
              color={channelIconColor(
                configuredSocials.facebook,
                selectedSocial === "facebook",
                FACEBOOK_COLOR
              )}
            />
          }
        />
        <FilterOption
          enabled={configuredSocials.instagram}
          selected={selectedSocial === "instagram"}
          label=""
          onClick={() => setSelectedSocial("instagram")}
          className="webchat-inbox-toolbar__channel"
          icon={
            <FontAwesomeIcon
              className="mx-1"
              icon={["fab", "instagram"]}
              size="lg"
              color={channelIconColor(
                configuredSocials.instagram,
                selectedSocial === "instagram",
                INSTAGRAM_COLOR
              )}
            />
          }
        />
        <FilterOption
          enabled={configuredSocials.linkedin}
          label=""
          onClick={() => setSelectedSocial("linkedin")}
          selected={selectedSocial === "linkedin"}
          className="webchat-inbox-toolbar__channel"
          icon={
            <FontAwesomeIcon
              className="mx-1"
              icon={["fab", "linkedin"]}
              size="lg"
              color={channelIconColor(
                configuredSocials.linkedin,
                selectedSocial === "linkedin",
                LINKED_IN_COLOR
              )}
            />
          }
        />
        <FilterOption
          enabled={configuredSocials.linkedinBusiness}
          label=""
          onClick={() => setSelectedSocial("linkedinBusiness")}
          selected={selectedSocial === "linkedinBusiness"}
          className="webchat-inbox-toolbar__channel"
          icon={
            <FontAwesomeIcon
              className="mx-1"
              icon={["fab", "linkedin"]}
              size="lg"
              color={channelIconColor(
                configuredSocials.linkedinBusiness,
                selectedSocial === "linkedinBusiness",
                LINKED_IN_BUSINESS_COLOR
              )}
            />
          }
        />
        <Button
          type="button"
          color="link"
          className="webchat-inbox-toolbar__more p-1"
          onClick={onOpenAdvancedFilters}
          title={intl.formatMessage({ id: "app.chat.inboxMoreFilters" })}
        >
          <FontAwesomeIcon icon={["fas", "plus"]} className="text-secondary" />
        </Button>
      </div>

      <div className="webchat-inbox-toolbar__search-row">
        <InputWembii
          type="text"
          placeholder={intl.formatMessage({
            id: "app.chat.inboxSearchPlaceholder",
          })}
          rounded
          showShadow
          className="webchat-inbox-toolbar__search flex-grow-1"
          style={{ borderRadius: "12px" }}
          value={searchText}
          onChange={(v) => onSearchChange(v)}
          icon={
            <FontAwesomeIcon
              icon={["fas", "search"]}
              size="1x"
              color="grey"
              className="pointer"
            />
          }
        />
        <button
          type="button"
          className="webchat-inbox-toolbar__filter-btn btn btn-light border shadow-sm"
          onClick={onOpenAdvancedFilters}
          aria-label={intl.formatMessage({ id: "app.chat.inboxMoreFilters" })}
        >
          <FontAwesomeIcon icon={["fas", "sliders-h"]} className="text-primary" />
        </button>
      </div>

      <div className="webchat-inbox-toolbar__tabs" role="tablist">
        {tabs.map(({ id, labelId }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={listFilter === id}
            className={cn("webchat-inbox-toolbar__tab", {
              "webchat-inbox-toolbar__tab--active": listFilter === id,
            })}
            onClick={() => onListFilterChange(id)}
          >
            <span className="webchat-inbox-toolbar__tab-label">
              {intl.formatMessage({ id: labelId })}
            </span>
            {id === "unread" && hasUnread && (
              <span className="webchat-inbox-toolbar__tab-dot" aria-hidden />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
